
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

interface BecomeHostSectionProps {
  userId: string;
}

export const BecomeHostSection = ({ userId }: BecomeHostSectionProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const handleBecomeHost = async () => {
    try {
      setIsLoading(true);
      
      // Update the profile's role in the database
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ role: 'host' })
        .eq('id', userId);

      if (profileError) {
        console.error('Error updating profile:', profileError);
        throw profileError;
      }

      // Update the user's role in the metadata
      const { error: metadataError } = await supabase.auth.updateUser({
        data: { role: 'host' }
      });

      if (metadataError) {
        console.error('Error updating user metadata:', metadataError);
        throw metadataError;
      }

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ['profile', userId] });
      await queryClient.invalidateQueries({ queryKey: ['session'] });
      
      toast({
        title: "Success!",
        description: "You are now a host. Let's set up your hosting profile.",
      });
      
      // Force reload to ensure all components pick up the new host status
      window.location.href = '/profile/edit';
    } catch (error) {
      console.error('Error becoming host:', error);
      toast({
        title: "Error",
        description: "Failed to become a host. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="border-t pt-6 mt-6">
      <h3 className="font-semibold mb-4">Want to become a host?</h3>
      <p className="text-sm text-gray-600 mb-4">
        As a host, you can create availability slots, manage your booking calendar, 
        and share your local expertise with travelers.
      </p>
      <Button 
        onClick={handleBecomeHost}
        className="w-full"
        disabled={isLoading}
      >
        {isLoading ? 'Processing...' : 'Become a Host'}
      </Button>
    </div>
  );
};
